/* eslint-disable import/prefer-default-export */

/**
 * Normalizes a plain JSON query tree before loadTree/checkTree so field
 * switches (e.g. datetime → text) do not leave an operator that is invalid
 * for the new field — RAQB would null the operator and strip values, which
 * breaks the builder.
 */
import {
    extendConfig,
    getFieldConfig,
    getFirstOperator,
    getOperatorConfig,
    getOperatorsForField,
} from 'react-awesome-query-builder/lib/utils/configUtils';

function normalizeArraysToCardinality(next, cardinality, correctType) {
    let out = next;

    if (cardinality === 0) {
        if (
            (out.valueType && out.valueType.length) ||
            (out.valueSrc && out.valueSrc.length) ||
            (out.valueError && out.valueError.length)
        ) {
            out = {
                ...out,
                value: [],
                valueType: [],
                valueSrc: [],
                valueError: [],
            };
        }
        return out;
    }

    const padType = (arr, fill) => {
        if (!Array.isArray(arr)) return Array(cardinality).fill(fill);
        const a = [...arr];
        const last = a.length ? a[a.length - 1] : fill;
        while (a.length < cardinality) {
            a.push(last);
        }
        return a.slice(0, cardinality);
    };

    if (correctType) {
        const vt = padType(out.valueType, correctType).map((vt) =>
            vt !== correctType ? correctType : vt
        );
        if (
            vt.length !== (out.valueType || []).length ||
            vt.some((v, i) => v !== out.valueType[i])
        ) {
            out = { ...out, valueType: vt };
        }
    }

    const defaultSrc =
        Array.isArray(out.valueSrc) && out.valueSrc.length
            ? out.valueSrc[0]
            : 'value';
    const vs = padType(out.valueSrc, defaultSrc);
    if (
        vs.length !== (out.valueSrc || []).length ||
        vs.some((v, i) => v !== out.valueSrc[i])
    ) {
        out = { ...out, valueSrc: vs };
    }

    if (Array.isArray(out.valueError)) {
        const ve = padType(out.valueError, null);
        if (
            ve.length !== out.valueError.length ||
            ve.some((v, i) => v !== out.valueError[i])
        ) {
            out = { ...out, valueError: ve };
        }
    }

    return out;
}

function normalizeRuleProperties(properties, config) {
    const { field } = properties;
    if (!field) return properties;

    const fieldDef = getFieldConfig(field, config);
    if (!fieldDef) return properties;

    let next = { ...properties };

    const correctType = fieldDef.type;

    const opDef = next.operator
        ? getOperatorConfig(config, next.operator, field)
        : null;
    let cardinality =
        opDef && typeof opDef.cardinality === 'number'
            ? opDef.cardinality
            : null;
    if (cardinality == null && Array.isArray(next.value)) {
        cardinality = next.value.length > 0 ? next.value.length : 1;
    }
    if (cardinality == null) {
        cardinality = 1;
    }

    next = normalizeArraysToCardinality(next, cardinality, correctType);

    const availOps = getOperatorsForField(config, field);
    if (!availOps || availOps.length === 0) return next;

    const op = next.operator;
    if (op && availOps.indexOf(op) !== -1) return next;

    const newOp = getFirstOperator(config, field) || availOps[0];
    return { ...next, operator: newOp };
}

function walk(node, config) {
    if (!node || !node.type) return node;

    if (node.type === 'rule' && node.properties) {
        return {
            ...node,
            properties: normalizeRuleProperties(node.properties, config),
        };
    }

    if (node.children1) {
        const keys = Object.keys(node.children1);
        let changed = false;
        const newKids = {};
        keys.forEach((k) => {
            const fixed = walk(node.children1[k], config);
            if (fixed !== node.children1[k]) changed = true;
            newKids[k] = fixed;
        });
        if (changed) return { ...node, children1: newKids };
    }

    return node;
}

/**
 * @param {object} treeJson - plain JS tree from getTree / persisted value
 * @param {object} config - same config passed to Query (fields, types, …)
 * @returns {object} normalized tree JSON
 */
export function normalizeQueryTreeForRaqb(treeJson, config) {
    if (!treeJson || !config) return treeJson;
    extendConfig(config);
    return walk(treeJson, config);
}
